// changed by Cursor: display notes by skill with loading state
import React, { useState, useEffect, useRef } from 'react';
// createPortal no longer needed - buttons rendered inline
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { lastOpenedNoteState } from '../../../recoil/atoms';
import { ArrowLeft, CircleNotch, Flag } from 'phosphor-react';
import { motion, AnimatePresence } from 'framer-motion';
import SkillPractice from './SkillPractice';
import DarkModeToggle from '../DarkModeToggle';
import ReportNoteModal from './ReportNoteModal';
import FloatingNavButtons from '../shared/FloatingNavButtons';

// changed by Cursor: map annotations for quick lookup
const buildAnnotationsMap = (annotations = []) => {
  return annotations.reduce((acc, annotation) => {
    const annId = annotation?.['ann-id'] || annotation?.annId || annotation?.id;
    const streamUrl = annotation?.['stream-url'] || annotation?.streamUrl || annotation?.stream_url;
    if (!annId || !streamUrl || (annotation?.type && annotation.type !== 'audio')) {
      return acc;
    }

    acc[annId] = {
      ...annotation,
      annId,
      streamUrl,
    };
    return acc;
  }, {});
};

// Normalize root wrapper spacing from CMS/editor HTML so card padding stays consistent.
const normalizeRootWrapperSpacing = (doc) => {
  const body = doc?.body;
  if (!body || body.children.length !== 1) {
    return;
  }

  const root = body.children[0];
  if (!root || !['DIV', 'SECTION', 'ARTICLE', 'MAIN'].includes(root.tagName)) {
    return;
  }

  const inlineStyle = (root.getAttribute('style') || '').toLowerCase();
  if (inlineStyle) {
    const hasSpacing = inlineStyle.includes('padding') || inlineStyle.includes('margin');
    if (hasSpacing) {
      root.style.padding = '0';
      root.style.margin = '0';
    }
  }

  const spacingOrCardClasses = [
    /^p-\d+$/,
    /^px-\d+$/,
    /^py-\d+$/,
    /^pt-\d+$/,
    /^pr-\d+$/,
    /^pb-\d+$/,
    /^pl-\d+$/,
    /^m-\d+$/,
    /^mx-\d+$/,
    /^my-\d+$/,
    /^mt-\d+$/,
    /^mr-\d+$/,
    /^mb-\d+$/,
    /^ml-\d+$/,
    /^bg-(white|gray-\d+|slate-\d+)$/,
    /^rounded(?:-.+)?$/,
    /^shadow(?:-.+)?$/,
  ];

  if (!root.classList || root.classList.length === 0) {
    return;
  }

  root.classList.forEach((className) => {
    const shouldRemove = spacingOrCardClasses.some((pattern) => pattern.test(className));
    if (shouldRemove) {
      root.classList.remove(className);
    }
  });
};

const GRAY_TEXT_CLASSES = [
  'text-slate-400', 'text-slate-500', 'text-slate-600', 'text-slate-700', 'text-slate-800',
  'text-gray-400', 'text-gray-500', 'text-gray-600', 'text-gray-700', 'text-gray-800',
  'text-neutral-400', 'text-neutral-500', 'text-neutral-600', 'text-neutral-700', 'text-neutral-800',
];

const HIGHLIGHT_BG_CLASS_PATTERN = /^bg-(green|yellow|pink|rose|red|amber|orange|lime|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia)/;

const shouldPreserveCmsNodeStyles = (node) => {
  if (!node?.getAttribute) return true;
  if (node.tagName === 'MARK') return true;
  if (node.classList?.contains('note-tag')) return true;
  if (node.hasAttribute('ann-id') || node.hasAttribute('data-ann-id')) return true;
  if (node.classList && Array.from(node.classList).some((cls) => HIGHLIGHT_BG_CLASS_PATTERN.test(cls))) {
    return true;
  }
  return false;
};

// Strip CMS inline gray/dark colors & cream backgrounds so theme CSS controls contrast.
const normalizeCmsNoteStyles = (doc) => {
  doc.querySelectorAll('*').forEach((node) => {
    if (shouldPreserveCmsNodeStyles(node)) return;

    if (node.hasAttribute('color')) {
      node.removeAttribute('color');
    }

    GRAY_TEXT_CLASSES.forEach((cls) => node.classList.remove(cls));

    if (!node.hasAttribute('style')) return;

    node.style.removeProperty('color');

    const bgColor = (node.style.getPropertyValue('background-color') || '').trim();
    const bg = (node.style.getPropertyValue('background') || '').trim();
    if (bgColor || bg) {
      node.style.removeProperty('background-color');
      node.style.removeProperty('background');
    }

    if (!node.getAttribute('style')?.trim()) {
      node.removeAttribute('style');
    }
  });
};

// changed by Cursor: add annotation metadata into HTML
const enhanceContentWithAnnotations = (html = '', annotationsMap) => {
  if (!html) {
    return html;
  }

  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return html;
  }

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Keep visual spacing consistent even when backend note has wrapper padding.
    normalizeRootWrapperSpacing(doc);
    normalizeCmsNoteStyles(doc);

    if (annotationsMap && Object.keys(annotationsMap).length > 0) {
      const nodes = doc.querySelectorAll('[ann-id], [data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('ann-id') || node.getAttribute('data-ann-id');
        if (!annId || !annotationsMap[annId]) {
          return;
        }

        node.setAttribute('data-ann-id', annId);
        node.classList.add('note-annotation');

        if (!node.hasAttribute('tabindex')) {
          node.setAttribute('tabindex', '0');
        }

        if (!node.hasAttribute('role')) {
          node.setAttribute('role', 'button');
        }

        node.setAttribute(
          'aria-label',
          `Play audio for ${node.textContent?.trim() || 'this word'}`
        );
      });
    }

    return doc.body.innerHTML;
  } catch (error) {
    console.warn('NotesView: failed to enhance content with annotations', error);
    return html;
  }
};

// changed by Cursor: utility to safely find annotation trigger target
const findAnnotationTrigger = (event) => {
  const target = event.target;
  if (target && typeof target.closest === 'function') {
    return target.closest('[data-ann-id]');
  }

  const parentElement = target?.parentElement;
  if (parentElement && typeof parentElement.closest === 'function') {
    return parentElement.closest('[data-ann-id]');
  }

  return null;
};

// Helper function to detect webview
const isWebView = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (localStorage.getItem('isWebView') === 'true') {
    return true;
  }
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webview') === 'true') {
    return true;
  }
  if (window.ReactNativeWebView) {
    return true;
  }
  if (window.__WEBVIEW__ || window.isWebView) {
    return true;
  }
  if (window.webkit && window.webkit.messageHandlers && Object.keys(window.webkit.messageHandlers).length > 0) {
    return true;
  }
  const userAgent = navigator.userAgent || '';
  const userAgentLower = userAgent.toLowerCase();
  if (userAgentLower.includes('wv') && !userAgentLower.includes('chrome')) {
    return true;
  }
  return false;
};

const NotesView = ({ title = "Reading Materials", notes = [], loading = false, skillId = null, lessonId = null, skills = [], currentSkillId = null, onSkillChange = null, onBack = null, isMobile = false, onReadingMaterialClick = null }) => {
  const [expandedNotes, setExpandedNotes] = useState({});
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [reportNoteId, setReportNoteId] = useState(null);
  const notesAudioRef = useRef(null);
  const activeAnnotationRef = useRef(null);
  const notesArray = Array.isArray(notes) ? notes : [];
  const setLastOpenedNote = useSetRecoilState(lastOpenedNoteState);
  const lastOpenedNote = useRecoilValue(lastOpenedNoteState);
  const hasRestoredRef = useRef(false);
  const lastSkillLessonRef = useRef({ skillId: null, lessonId: null });
  const noteItemContentRefs = useRef({});
  
  // Fallback: load dari localStorage jika Recoil belum ter-load
  const getLastOpenedNote = () => {
    if (lastOpenedNote) {
      return lastOpenedNote;
    }
    // Fallback ke localStorage
    try {
      const saved = localStorage.getItem('lastOpenedNote');
      const parsed = saved ? JSON.parse(saved) : null;
      console.log('📖 NotesView: Loaded from localStorage:', parsed);
      return parsed;
    } catch (e) {
      console.warn('📖 NotesView: Failed to parse lastOpenedNote from localStorage:', e);
      return null;
    }
  };
  
  // Reset restore flag hanya jika skillId atau lessonId benar-benar berubah ke value BARU
  // JANGAN reset jika kembali ke value yang sama dengan yang di-restore
  // Juga set initial flag jika skill/lesson cocok dengan yang di-restore
  useEffect(() => {
    if (!skillId || !lessonId) return;
    
    const prevSkillId = lastSkillLessonRef.current.skillId;
    const prevLessonId = lastSkillLessonRef.current.lessonId;
    
    // Cek apakah skill/lesson saat ini sama dengan yang di-restore
    const savedLastNote = getLastOpenedNote();
    const matchesRestoredSkillLesson = savedLastNote && 
                                      savedLastNote.skillId && 
                                      savedLastNote.lessonId &&
                                      String(savedLastNote.skillId) === String(skillId) &&
                                      String(savedLastNote.lessonId) === String(lessonId);
    
    console.log('📖 NotesView: Reset flag check - savedLastNote:', savedLastNote, 'skillId:', skillId, 'lessonId:', lessonId, 'matchesRestored:', matchesRestoredSkillLesson, 'hasRestored:', hasRestoredRef.current);
    
    // Jika skill/lesson cocok dengan yang di-restore, set flag jika belum di-set
    if (matchesRestoredSkillLesson) {
      if (!hasRestoredRef.current) {
        // Set flag untuk pertama kali
        hasRestoredRef.current = true;
        console.log('📖 NotesView: ✅ Setting restore flag - matches restored value', { skillId, lessonId });
      } else {
        console.log('📖 NotesView: ✅ Skill/Lesson matches restored value, keeping restore flag', { skillId, lessonId });
      }
      lastSkillLessonRef.current = { skillId, lessonId };
      return; // Jangan reset, langsung return
    }
    
    // Hanya reset jika benar-benar berubah (dari satu value ke value lain, bukan dari null ke value)
    const skillChanged = prevSkillId !== null && prevSkillId !== undefined && 
                         skillId !== null && skillId !== undefined && 
                         String(prevSkillId) !== String(skillId);
    const lessonChanged = prevLessonId !== null && prevLessonId !== undefined && 
                         lessonId !== null && lessonId !== undefined && 
                         String(prevLessonId) !== String(lessonId);
    
    if (skillChanged || lessonChanged) {
      // Berubah ke value yang berbeda dari yang di-restore, reset flag
      hasRestoredRef.current = false;
      lastSkillLessonRef.current = { skillId, lessonId };
      console.log('📖 NotesView: Skill/Lesson changed to different value, resetting restore flag', { prevSkillId, skillId, prevLessonId, lessonId, matchesRestoredSkillLesson });
    } else if ((skillId !== null && skillId !== undefined) || (lessonId !== null && lessonId !== undefined)) {
      // Update ref tanpa reset flag jika hanya initial set atau fetch ulang dengan value yang sama
      if (!hasRestoredRef.current || String(lastSkillLessonRef.current.skillId) !== String(skillId) || String(lastSkillLessonRef.current.lessonId) !== String(lessonId)) {
        lastSkillLessonRef.current = { skillId, lessonId };
      }
    }
  }, [skillId, lessonId]);

  // Auto-expand all notes when notes change, atau restore note terakhir yang dibuka
  useEffect(() => {
    if (loading) {
      // Jangan update saat masih loading
      return;
    }
    
    if (notesArray.length === 0) {
      setExpandedNotes({});
      setCurrentNoteId(null);
      hasRestoredRef.current = false;
      return;
    }
    
    // Jika sudah restore dan notes masih sama (fetch ulang), jangan overwrite
    console.log('📖 NotesView: Checking restore guard - hasRestored:', hasRestoredRef.current, 'skillId:', skillId, 'lessonId:', lessonId, 'lastSkillLesson:', lastSkillLessonRef.current);
    
    // Cek apakah ada savedLastNote yang cocok dengan skill/lesson saat ini
    const savedLastNote = getLastOpenedNote();
    const shouldRestore = savedLastNote && 
                         savedLastNote.skillId && 
                         savedLastNote.lessonId && 
                         savedLastNote.noteId &&
                         String(savedLastNote.skillId) === String(skillId) && 
                         String(savedLastNote.lessonId) === String(lessonId);
    
    // Jika sudah restore sebelumnya untuk skill/lesson ini, re-apply state
    if (hasRestoredRef.current) {
      const skillLessonMatch = String(lastSkillLessonRef.current.skillId) === String(skillId) && 
                               String(lastSkillLessonRef.current.lessonId) === String(lessonId);
      
      console.log('📖 NotesView: Guard check - skillLessonMatch:', skillLessonMatch, 'hasRestored:', hasRestoredRef.current);
      
      if (skillLessonMatch && notesArray.length > 0) {
        // Re-apply restore state: expand semua notes
        const restoreState = {};
        let firstNoteId = null;
        notesArray.forEach(note => {
          const noteId = note.id || note._id;
          if (noteId) {
            restoreState[noteId] = true;
            if (!firstNoteId) {
              firstNoteId = noteId;
            }
          }
        });
        
        // Set currentNoteId jika belum ada
        if (!currentNoteId && firstNoteId) {
          setCurrentNoteId(firstNoteId);
        }
        
        console.log('📖 NotesView: ✅ Re-applying restored state after fetch (expand all):', restoreState, 'hasRestored:', hasRestoredRef.current);
        setExpandedNotes(restoreState);
        return; // Jangan overwrite dengan logic baru
      } else if (!skillLessonMatch) {
        // Skill/Lesson berubah, reset flag
        console.log('📖 NotesView: ⚠️ Skill/Lesson changed, resetting flags');
        hasRestoredRef.current = false;
      }
    }

    const updatedState = {};
    
    // Cek apakah ada lastOpenedNote yang cocok dengan skillId dan lessonId saja
    // savedLastNote sudah dideklarasikan di atas (line 170)
    if (savedLastNote && savedLastNote.skillId && savedLastNote.lessonId &&
        String(savedLastNote.skillId) === String(skillId) && 
        String(savedLastNote.lessonId) === String(lessonId)) {
      
      // Jika sudah pernah restore untuk skill/lesson ini, jangan overwrite
      if (hasRestoredRef.current && 
          lastSkillLessonRef.current.skillId === skillId && 
          lastSkillLessonRef.current.lessonId === lessonId) {
        console.log('📖 NotesView: Already restored for this skill/lesson, keeping state');
        return; // Jangan overwrite state yang sudah di-restore
      }
      
      // Expand semua notes untuk skill/lesson yang cocok
      let firstNoteId = null;
      notesArray.forEach(note => {
        const noteId = note.id || note._id;
        if (noteId) {
          updatedState[noteId] = true;
          if (!firstNoteId) {
            firstNoteId = noteId;
          }
        }
      });
      
      // Set currentNoteId ke note pertama atau note yang di-restore
      if (savedLastNote && savedLastNote.noteId) {
        const restoredNoteId = savedLastNote.noteId;
        const noteExists = notesArray.some(note => {
          const noteId = note.id || note._id;
          return noteId && String(noteId) === String(restoredNoteId);
        });
        if (noteExists) {
          setCurrentNoteId(restoredNoteId);
        } else if (firstNoteId) {
          setCurrentNoteId(firstNoteId);
        }
      } else if (firstNoteId) {
        setCurrentNoteId(firstNoteId);
      }
      
      hasRestoredRef.current = true;
      // CRITICAL: Set lastSkillLessonRef SEBELUM setExpandedNotes untuk prevent reset
      lastSkillLessonRef.current = { skillId, lessonId };
      console.log('📖 NotesView: ✅ Restoring all notes for skill:', skillId, 'lesson:', lessonId, 'updatedState:', updatedState);
      console.log('📖 NotesView: ✅ Set restore flags - hasRestored:', hasRestoredRef.current, 'lastSkillLesson:', lastSkillLessonRef.current);
      
      // Sync ke Recoil jika belum ada
      if (!lastOpenedNote) {
        setLastOpenedNote(savedLastNote);
      }
      
      // Pastikan data tersimpan di localStorage
      try {
        localStorage.setItem('lastOpenedNote', JSON.stringify(savedLastNote));
      } catch (e) {
        console.warn('Failed to save lastOpenedNote to localStorage:', e);
      }
      
      // Set state dan return early untuk mencegah overwrite
      setExpandedNotes(updatedState);
      return;
    } else {
      // Jika tidak ada lastOpenedNote atau skillId tidak cocok, expand semua notes
      let firstNoteId = null;
      notesArray.forEach(note => {
        const noteId = note.id || note._id;
        if (noteId) {
          updatedState[noteId] = true; // Auto-expand all notes
          if (!firstNoteId) {
            firstNoteId = noteId;
          }
        }
      });
      
      // Set currentNoteId ke note pertama
      if (firstNoteId) {
        setCurrentNoteId(firstNoteId);
      }
      
      hasRestoredRef.current = false;
      
      // Simpan note pertama yang di-expand sebagai lastOpenedNote jika skillId dan lessonId tersedia
      // Hanya jika tidak ada savedLastNote (user baru buka, bukan restore)
      if (notesArray.length > 0 && skillId && lessonId && !savedLastNote) {
        const firstNote = notesArray[0];
        const firstNoteId = firstNote.id || firstNote._id;
        if (firstNoteId) {
          const lastNote = {
            noteId: firstNoteId,
            skillId: skillId,
            lessonId: lessonId
          };
          setLastOpenedNote(lastNote);
          try {
            localStorage.setItem('lastOpenedNote', JSON.stringify(lastNote));
            console.log('📖 NotesView: Auto-saving first note as lastOpenedNote:', lastNote);
          } catch (e) {
            console.warn('Failed to save lastOpenedNote to localStorage:', e);
          }
        }
      }
    }

    // Set state
    console.log('📖 NotesView: Setting expandedNotes state:', updatedState, 'hasRestored:', hasRestoredRef.current);
    setExpandedNotes(updatedState);
  }, [notes, loading, lastOpenedNote, skillId, lessonId, setLastOpenedNote]); // expandedNotes tidak perlu di dependency untuk avoid loop

  useEffect(() => {
    return () => {
      if (notesAudioRef.current) {
        notesAudioRef.current.pause();
        notesAudioRef.current = null;
      }
    };
  }, []);
  
  // Set currentNoteId ke note pertama jika belum ada yang di-set dan ada notes
  useEffect(() => {
    if (!loading && notesArray.length > 0 && !currentNoteId) {
      const firstNote = notesArray[0];
      const firstNoteId = firstNote.id || firstNote._id;
      if (firstNoteId) {
        console.log('📖 NotesView: Setting currentNoteId to first note:', firstNoteId);
        setCurrentNoteId(firstNoteId);
      }
    }
  }, [notesArray, loading, currentNoteId]);
  

  const toggleNote = (noteId) => {
    setExpandedNotes(prev => {
      const newState = {
        ...prev,
        [noteId]: !prev[noteId]
      };
      
      // Set currentNoteId jika note di-expand
      if (newState[noteId]) {
        setCurrentNoteId(noteId);
        // Call callback ketika user klik reading material (note di-expand)
        if (onReadingMaterialClick && typeof onReadingMaterialClick === 'function') {
          onReadingMaterialClick();
        }
      } else {
        // Jika note di-collapse dan itu adalah currentNoteId, clear atau set ke note expanded lainnya
        if (currentNoteId === noteId) {
          const expandedNoteIds = Object.keys(newState).filter(id => newState[id]);
          if (expandedNoteIds.length > 0) {
            setCurrentNoteId(expandedNoteIds[0]);
          } else {
            setCurrentNoteId(null);
          }
        }
      }
      
      // Simpan skillId dan lessonId saat ada note yang expanded
      if (skillId && lessonId) {
        // Cek apakah ada note yang expanded setelah toggle
        const hasExpandedNote = Object.keys(newState).some(id => newState[id]);
        
        // Jika ada note yang expanded, simpan skillId dan lessonId saja
        if (hasExpandedNote) {
          const lastContext = {
            skillId: skillId,
            lessonId: lessonId
          };
          
          // Update Recoil state
          setLastOpenedNote(lastContext);
          
          // Update ref untuk tracking
          lastSkillLessonRef.current = { skillId, lessonId };
          
          // Simpan juga ke localStorage untuk persist across page reload
          try {
            localStorage.setItem('lastOpenedNote', JSON.stringify(lastContext));
            console.log('📖 NotesView: ✅ Saved last context (from toggle):', lastContext);
          } catch (e) {
            console.warn('📖 NotesView: Failed to save lastOpenedNote to localStorage:', e);
          }
        } else {
          // Semua note di-collapse, clear state
          console.log('📖 NotesView: All notes collapsed, clearing saved state');
          setLastOpenedNote(null);
          try {
            localStorage.removeItem('lastOpenedNote');
          } catch (e) {
            console.warn('📖 NotesView: Failed to remove lastOpenedNote from localStorage:', e);
          }
        }
      }
      
      return newState;
    });
  };
  
  // Helper untuk check mobile dan guard scroll behavior
  const isMobileDevice = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  };

  // Helper: dapatkan elemen scroll (mobile overlay pakai .mobile-notes-inline-content)
  const getScrollContainer = () => {
    if (!notesViewRef.current) return null;
    return notesViewRef.current.closest('.mobile-notes-inline-content') ||
      notesViewRef.current.closest('.mobile-reading-view') ||
      notesViewRef.current.closest('.notes-view-wrapper') ||
      notesViewRef.current.querySelector('.notes-content');
  };

  // Handler untuk navigate ke note lain dari NextPrev
  const handleNoteChange = (noteId) => {
    setCurrentNoteId(noteId);
    
    // Guard: Jangan scroll di mobile kecuali explicit user action (ini adalah explicit action dari NextPrev button)
    // Scroll ke note yang dipilih
    const noteElement = document.querySelector(`[data-note-id="${noteId}"]`);
    if (noteElement) {
      if (isMobileDevice() && notesViewRef.current) {
        const container = getScrollContainer();
        if (container) {
          const elementRect = noteElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2);
          container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
        } else {
          noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleExpandNote = (noteId) => {
    setExpandedNotes(prev => {
      const newState = {
        ...prev,
        [noteId]: true
      };
      return newState;
    });
    
    // Scroll ke note setelah expand (ini adalah explicit user action)
    setTimeout(() => {
      const noteElement = document.querySelector(`[data-note-id="${noteId}"]`);
      if (noteElement) {
        if (isMobileDevice() && notesViewRef.current) {
          const container = getScrollContainer();
          if (container) {
            const elementRect = noteElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - (containerRect.height / 2) + (elementRect.height / 2);
            container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
          } else {
            noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          noteElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const highlightAnnotation = (element) => {
    if (!element) {
      return;
    }

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('note-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('note-annotation-active');
  };

  const playAnnotationAudio = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) {
      return;
    }

    if (!notesAudioRef.current) {
      notesAudioRef.current = new Audio();
      notesAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('note-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }

    const audioEl = notesAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl
      .play()
      .catch((error) => {
        console.warn('NotesView: failed to play annotation audio', error);
      });
  };

  const handleAnnotationTrigger = (event, annotationsMap) => {
    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    playAnnotationAudio(annotation);
  };

  const handleAnnotationKeyDown = (event, annotationsMap) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event, annotationsMap);
  };

  console.log('NotesView: notes received:', notes, 'converted to array:', notesArray);

  // Logic untuk NextPrev buttons
  const skillsArray = Array.isArray(skills) ? skills : [];
  console.log('NotesView: skillsArray.length:', skillsArray.length, 'skills:', skills);
  const currentIndex = skillsArray.findIndex(skill => {
    const sid = skill.id || skill._id;
    return sid != null && String(sid).trim() === String(currentSkillId || '').trim();
  });

  let effectiveCurrentIndex = currentIndex;
  if (currentIndex === -1 && skillsArray.length > 0) {
    effectiveCurrentIndex = 0;
  }

  const hasPrevious = effectiveCurrentIndex > 0;
  const hasNext = effectiveCurrentIndex >= 0 && effectiveCurrentIndex < skillsArray.length - 1;

  // changed by Cursor: scroll to top of notes-view section on Next/Prev
  // CRITICAL: On mobile the page itself scrolls (window), not .mobile-notes-inline-content
  const scrollToNotesTop = () => {
    const doScroll = () => {
      const notesView = notesViewRef.current;
      if (!notesView) return;
      // Scroll window so that notes-view top is at the top of viewport
      const rect = notesView.getBoundingClientRect();
      const targetY = window.scrollY + rect.top;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'auto' });
    };
    doScroll();
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 50);
    setTimeout(doScroll, 200);
    setTimeout(doScroll, 500);
  };

  const handlePrevious = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!hasPrevious || !onSkillChange) return;
    const prevSkill = skillsArray[effectiveCurrentIndex - 1];
    const prevSkillId = prevSkill?.id ?? prevSkill?._id;
    if (prevSkillId != null) {
      onSkillChange(prevSkillId);
      scrollToNotesTop();
    }
  };

  const handleNext = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!hasNext || !onSkillChange) return;
    const nextSkill = skillsArray[effectiveCurrentIndex + 1];
    const nextSkillId = nextSkill?.id ?? nextSkill?._id;
    if (nextSkillId != null) {
      onSkillChange(nextSkillId);
      scrollToNotesTop();
    }
  };


  // Ref untuk notes-view container
  const notesViewRef = useRef(null);
  /** Mobile only: nav buttons (Skill Practice + Prev/Next/Up) visible on hover/tap */
  const [showFixedNavRevealed, setShowFixedNavRevealed] = useState(false);
  const hideNavTimeoutRef = useRef(null);
  // changed by Cursor: ref for notes-content + fixed button visibility
  const notesContentRef = useRef(null);
  const [showMobileButtons, setShowMobileButtons] = useState(true);

  // Show fixed nav buttons (Skill Practice, Previous, Next, Up) only when scroll is in range
  useEffect(() => {
    if (skillsArray.length <= 1 && !skillId) return;
    const handleScroll = () => {
      const content = notesContentRef.current;
      if (!content) { setShowMobileButtons(false); return; }
      const contentRect = content.getBoundingClientRect();
      const btnY = window.innerHeight - 84;
      const visible = contentRect.top < 200 && contentRect.bottom > btnY;
      setShowMobileButtons(visible);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [skillsArray.length, skillId]);

  // Setiap masuk ke reading materials (mobile) atau ganti skill, scroll ke top of notes-view
  // changed by Cursor: scroll WINDOW (bukan container) karena page-level scroll di mobile
  useEffect(() => {
    if (!isMobile || !currentSkillId) return;
    const forceScrollTop = () => {
      const notesView = notesViewRef.current;
      if (!notesView) return;
      const rect = notesView.getBoundingClientRect();
      const targetY = window.scrollY + rect.top;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'auto' });
    };
    forceScrollTop();
    const id = requestAnimationFrame(forceScrollTop);
    const t1 = setTimeout(forceScrollTop, 50);
    const t2 = setTimeout(forceScrollTop, 200);
    const t3 = setTimeout(forceScrollTop, 500);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isMobile, currentSkillId, notesArray.length]);

  // changed by Cursor: label nomor skill sesuai urutan di tree
  const skillNumberLabel = skillsArray.length > 1 && effectiveCurrentIndex >= 0
    ? `${effectiveCurrentIndex + 1} of ${skillsArray.length}`
    : null;

  return (
    <div
      className={`notes-view flex-1 min-w-0 bg-[#faf7f2] dark:bg-[var(--card-bg)] rounded-[10px] max-md:rounded-none overflow-hidden max-md:overflow-visible shadow-[0_4px_16px_rgba(0,0,0,0.2)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.6),0_0_1px_rgba(0,0,0,0.3)] max-md:shadow-[0_4px_20px_rgba(0,0,0,0.25)] dark:max-md:shadow-[0_4px_24px_rgba(0,0,0,0.55)] border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)] flex flex-col relative${isMobile ? ' notes-view-mobile' : ''}`}
      ref={notesViewRef}
    >
      <div className={`bg-gradient-to-br from-[#12766d] via-[#236463] to-[#1d4746] dark:from-[#0d4f49] dark:via-[#153938] dark:to-[#0f2928] min-h-[44px] box-border flex items-center justify-between text-white${isMobile && onBack ? ' relative p-[12px_16px] border-b-[3px] border-[#12766d] dark:border-[var(--accent-teal)]' : ' p-[10px_16px]'}`}>
        {isMobile && !isWebView() && onBack ? (
          <>
            <div className="flex items-center flex-shrink-0 z-[1]">
              <button
                className="p-0 bg-transparent border-none text-white cursor-pointer transition-all duration-200 flex items-center justify-center hover:opacity-80 active:opacity-60 active:scale-95"
                onClick={onBack}
                aria-label="Back"
              >
                <ArrowLeft size={22} weight="bold" />
              </button>
            </div>

            {/* centered title + skill number */}
            <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
              <span className="text-[13px] font-bold text-white m-0 block">{title}</span>
              {skillNumberLabel && (
                <div className="text-white text-[0.75em] mt-0.5 opacity-85">{skillNumberLabel}</div>
              )}
            </div>

            {/* right side: theme toggle */}
            <div className="flex items-center gap-1 flex-shrink-0 z-[1] ml-auto">
              <DarkModeToggle variant="header" />
            </div>
          </>
        ) : (
          <>
            {/* desktop */}
            <div className="flex items-center gap-2 relative" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isMobile && !isWebView() && onBack && (
                  <button
                    className="p-0 bg-transparent border-none text-white cursor-pointer transition-all duration-200 flex items-center justify-center mr-1 hover:opacity-80 active:opacity-60 active:scale-95"
                    onClick={onBack}
                    aria-label="Back"
                  >
                    <ArrowLeft size={20} weight="bold" />
                  </button>
                )}
                <span className="text-[14px] font-bold max-md:text-[13px]">{title}</span>
              </div>
            </div>
            {skillNumberLabel && (
              <div style={{ color: '#ffffff', fontSize: '0.72em', lineHeight: 1 }}>
                {skillNumberLabel}
              </div>
            )}
            {/* skillpractice button desktop */}
            {skillId && <SkillPractice skillId={skillId} variant="header" />}
          </>
        )}
      </div>

      {/* Mobile: Skill Practice bar below header */}
      {isMobile && skillId && (
        <div className="p-[12px_16px] bg-[#faf7f2] dark:bg-[var(--bg-secondary)] border-b border-[rgba(18,118,109,0.15)] dark:border-[var(--border-color)]">
          <SkillPractice skillId={skillId} variant="below-header" />
        </div>
      )}

      <div className="notes-content p-0.5 bg-[#faf7f2] dark:bg-[var(--bg-secondary)] flex-1 overflow-y-auto max-md:pb-[100px] max-md:overflow-visible" ref={notesContentRef}>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 px-5 gap-3">
            <CircleNotch size={24} weight="bold" className="text-[#12766d] animate-spin" />
            <span className="text-[13px] text-[var(--text-secondary)]">Loading lessons...</span>
          </div>
        ) : notesArray.length === 0 ? (
          <div className="text-[14px] text-[var(--text-secondary)] text-center p-5">{!lessonId ? 'Choose the reading material.' : 'No reading materials available...'}</div>
        ) : (
          <>
            {/* changed by Cursor: unified bottom-right fixed nav for both desktop & mobile */}
            {skillsArray.length > 1 && showMobileButtons && (
              <FloatingNavButtons
                containerClassName="z-[10004] right-6 max-md:bottom-[84px] max-md:right-3"
                prevDisabled={!hasPrevious}
                nextDisabled={!hasNext}
                onPrev={handlePrevious}
                onNext={handleNext}
                onUp={scrollToNotesTop}
                prevLabel="Previous skill"
                nextLabel="Next skill"
                accentNext={hasNext}
              />
            )}
            <div className="flex flex-col gap-[10px] max-md:gap-2">
              {notesArray.map((note) => {
                const noteId = note.id || note._id;
                const annotationsMap = buildAnnotationsMap(note.annotations);
                const enhancedContent = enhanceContentWithAnnotations(
                  note.content || note.text || '',
                  annotationsMap
                );
                const hasAnnotations = annotationsMap && Object.keys(annotationsMap).length > 0;

                return (
                  <div key={noteId} className="rounded-[8px] overflow-hidden" data-note-id={noteId}>
                    <div className={`note-item-content relative p-[2px_4px] bg-[#faf7f2] dark:bg-[var(--bg-secondary)]${isMobile ? ' flex flex-col items-stretch' : ''}`}>
                      {noteId && (
                        <motion.button
                          type="button"
                          className="report-note-trigger absolute top-2 right-2 flex items-center gap-1.5 text-[12px] text-[var(--text-secondary)] transition-colors max-md:static max-md:self-end max-md:mb-1"
                          whileHover={{ scale: 1.03 }}
                          transition={{ duration: 0.15 }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setReportNoteId(noteId);
                          }}
                          aria-label="Report note"
                        >
                          <Flag size={12} weight="bold" />
                          <span>Report this note</span>
                        </motion.button>
                      )}
                      {/* changed by Cursor: apply Tailwind Typography classes */}
                      <div
                        className="note-item-text max-w-none"
                        onClick={hasAnnotations ? (event) => handleAnnotationTrigger(event, annotationsMap) : undefined}
                        onKeyDown={hasAnnotations ? (event) => handleAnnotationKeyDown(event, annotationsMap) : undefined}
                        dangerouslySetInnerHTML={{ __html: enhancedContent }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <ReportNoteModal
        noteId={reportNoteId}
        isOpen={reportNoteId !== null}
        onClose={() => setReportNoteId(null)}
      />
    </div>
  );
};

export default NotesView;
